import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import PageHeader from "@/components/ui/page-header";
import Dropdown from "@/components/ui/dropdown";
import {
  LeftChevron,
  ArrowLeft,
  ArrowRight,
  Download,
} from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { FilterField, SortOption } from "@/types/component";
import type {
  Poll,
  PollAttendee,
  GetAttendeesParams,
} from "../types/polls.types";
import { pollsApi } from "../services/polls.api";
import AttendeeCard from "../components/AttendeeCard";
import AttendeeAnswersDrawer from "../components/AttendeeAnswersDrawer";
import styles from "./PollAttendeesPage.module.scss";

const PAGE_SIZE_OPTIONS = [12, 24, 48];

function sortAttendees(
  data: PollAttendee[],
  sortValue: string,
): PollAttendee[] {
  const arr = [...data];
  switch (sortValue) {
    case "nameAsc":
      return arr.sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", "tr"),
      );
    case "nameDesc":
      return arr.sort((a, b) =>
        (b.name ?? "").localeCompare(a.name ?? "", "tr"),
      );
    case "progressDesc":
      return arr.sort((a, b) => b.percentage - a.percentage);
    case "progressAsc":
      return arr.sort((a, b) => a.percentage - b.percentage);
    default:
      return arr;
  }
}

const PollAttendeesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pollId } = useParams<{ pollId: string }>();

  const [poll, setPoll] = useState<Poll | null>(null);
  const [attendees, setAttendees] = useState<PollAttendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedAttendee, setSelectedAttendee] = useState<PollAttendee | null>(
    null,
  );
  const [sortBy, setSortBy] = useState<string>("nameAsc");
  const [filterStatus, setFilterStatus] = useState<string | undefined>(
    undefined,
  );

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch poll info
  useEffect(() => {
    if (!pollId) return;
    pollsApi
      .getById(pollId)
      .then(setPoll)
      .catch(() => setPoll(null));
  }, [pollId]);

  // Map UI sort value to API params (name | answerRate)
  const getSortParams = useCallback(() => {
    switch (sortBy) {
      case "nameAsc":
        return { sortBy: "name" as const, sortOrder: "asc" as const };
      case "nameDesc":
        return { sortBy: "name" as const, sortOrder: "desc" as const };
      case "progressDesc":
        return { sortBy: "answerRate" as const, sortOrder: "desc" as const };
      case "progressAsc":
        return { sortBy: "answerRate" as const, sortOrder: "asc" as const };
      default:
        return { sortBy: "name" as const, sortOrder: "asc" as const };
    }
  }, [sortBy]);

  // Fetch attendees (API pagination yok; client-side pagination)
  const fetchAttendees = useCallback(async () => {
    if (!pollId) return;
    setIsLoading(true);
    try {
      const sortParams = getSortParams();
      const completed =
        filterStatus === "katildi"
          ? true
          : filterStatus === "katilmadi"
            ? false
            : undefined;
      const params: GetAttendeesParams = {
        search: debouncedSearch,
        completed,
        ...sortParams,
      };
      const response = await pollsApi.getAttendees(pollId, params);
      const sorted = sortAttendees(response.data, sortBy);
      setAttendees(sorted);
      setTotalRecords(response.total);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching attendees:", error);
    } finally {
      setIsLoading(false);
    }
  }, [pollId, debouncedSearch, sortBy, filterStatus, getSortParams]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / pageSize)),
    [totalRecords, pageSize],
  );

  const displayedAttendees = useMemo(
    () =>
      attendees.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [attendees, currentPage, pageSize],
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleViewAnswers = (attendee: PollAttendee) => {
    setSelectedAttendee(attendee);
  };

  const handleExport = async () => {
    if (!pollId || !poll) return;
    try {
      const completed =
        filterStatus === "katildi"
          ? true
          : filterStatus === "katilmadi"
            ? false
            : undefined;
      const exportParams: GetAttendeesParams = {
        search: debouncedSearch,
        completed,
        ...getSortParams(),
      };
      const { data: allAttendees } = await pollsApi.getAttendees(
        pollId,
        exportParams,
      );
      const sorted = sortAttendees(allAttendees, sortBy);

      const headerNames = [
        t("polls.attendees.pdfName"),
        t("polls.attendees.pdfTitle"),
        t("polls.attendees.pdfProgress"),
        t("polls.attendees.pdfStatus"),
      ];
      const escapeCsv = (val: string) => {
        const s = String(val ?? "");
        if (/[;"\n,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const sep = ";";
      const header = headerNames.map(escapeCsv).join(sep);
      const rows = sorted.map((a) =>
        [
          a.name ?? "",
          a.title ?? "",
          `${a.answeredCount}/${a.totalQuestions} (%${a.percentage})`,
          a.status === "katildi"
            ? t("polls.attendees.participated")
            : t("polls.attendees.notParticipated"),
        ]
          .map(escapeCsv)
          .join(sep),
      );
      const csvContent = [header, ...rows].join("\r\n");
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (poll.pollName || "anket-katilimcilari").replace(
        /[^a-z0-9\u00C0-\u024F-]/gi,
        "-",
      );
      a.download = `${safeName}-katilimci-listesi.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Export CSV failed:", err);
    }
  };

  const handleSortChange = (option: { value?: string } | undefined) => {
    const value = option?.value ?? "nameAsc";
    setSortBy(value);
    setCurrentPage(1);
  };

  // Sort options
  const sortOptions: SortOption[] = [
    {
      label: t("polls.attendees.sort.nameAsc"),
      value: "nameAsc",
    },
    {
      label: t("polls.attendees.sort.nameDesc"),
      value: "nameDesc",
    },
    {
      label: t("polls.attendees.sort.progressDesc"),
      value: "progressDesc",
    },
    {
      label: t("polls.attendees.sort.progressAsc"),
      value: "progressAsc",
    },
  ];

  // Filter fields
  const filterFields: FilterField[] = [
    {
      name: "status",
      label: t("polls.attendees.filters.status"),
      type: "dropdown",
      placeholder: t("common.all"),
      options: [
        {
          label: t("polls.attendees.participated"),
          value: "katildi",
        },
        {
          label: t("polls.attendees.notParticipated"),
          value: "katilmadi",
        },
      ],
    },
  ];

  const breadcrumbItems = [
    {
      label: t("polls.title"),
      href: "/dashboard/polls",
    },
    { label: poll?.pollName || t("polls.attendees.title") },
  ];

  if (!poll && !isLoading) {
    return (
      <DashboardLayout>
        <Header title={t("polls.title")} />
        <div className={styles.loading}>
          {t("common.notFound")}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title={t("polls.title")} />
      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/dashboard/polls")}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className={styles.pageContainer}>
        <PageHeader
          searchPlaceholder={t("polls.attendees.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={handleSearch}
          showSort
          sortOptions={sortOptions}
          onSort={(opt) => handleSortChange(opt)}
          filters={filterFields}
          onFilterChange={(filters) =>
            setFilterStatus((filters.status as string) || undefined)
          }
          onAddNew={handleExport}
          addButtonLabel={t("polls.attendees.export")}
          addButtonIcon={<Download width={20} height={20} />}
          addButtonVariant="outlined"
        />

        {isLoading ? (
          <div className={styles.loading}>
            {t("common.loading")}
          </div>
        ) : attendees.length === 0 ? (
          <div className={styles.emptyState}>
            {t("polls.attendees.noData")}
          </div>
        ) : (
          <>
            <div className={styles.attendeeGrid}>
              {displayedAttendees.map((attendee) => (
                <AttendeeCard
                  key={attendee.id}
                  attendee={attendee}
                  onViewAnswers={handleViewAnswers}
                />
              ))}
            </div>

            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>
                  {t("pagination.show")}
                </span>
                <div className={styles.paginationPageSizeDropdown}>
                  <Dropdown
                    onSelect={(v) =>
                      handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                    }
                    options={PAGE_SIZE_OPTIONS.map((size) => ({
                      label: size.toString(),
                      value: size.toString(),
                    }))}
                    menuPosition="top"
                    selectedLabel={pageSize.toString()}
                    value={pageSize.toString()}
                  />
                </div>
                <span className={styles.paginationDetails}>
                  {t(
                    "pagination.details",
                    t("polls.paginationDetails"),
                    {
                      start: (currentPage - 1) * pageSize + 1,
                      end: Math.min(currentPage * pageSize, totalRecords),
                      total: totalRecords,
                    },
                  )}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(
                  currentPage,
                  totalPages,
                  handlePageChange,
                )}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ArrowRight
                    height={16}
                    width={16}
                    color={currentPage === totalPages ? "#ccc" : "#e7212b"}
                  />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <AttendeeAnswersDrawer
        isOpen={!!selectedAttendee}
        onClose={() => setSelectedAttendee(null)}
        attendee={selectedAttendee}
        pollId={pollId}
        pollName={poll?.pollName}
      />
    </DashboardLayout>
  );
};

export default PollAttendeesPage;
